import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Award, Trophy, Star, ChevronRight, X } from 'lucide-react';

// Types of notifications we support
const NOTIFICATION_TYPES = {
  ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
  RANK_UP: 'rank_up',
  STREAK_MILESTONE: 'streak_milestone',
  POINTS_EARNED: 'points_earned'
};

// Minimum points required to show a notification
const MIN_POINTS_FOR_NOTIFICATION = 20;

// Minimum time between notifications (in ms)
const NOTIFICATION_COOLDOWN = 12000; // 12 seconds

// Function to mark a notification as read in Firestore
const markNotificationAsRead = async (userId, notificationId) => {
  try {
    const notificationRef = doc(db, 'users', userId, 'events', notificationId);
    await updateDoc(notificationRef, {
      read: true
    });
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

const AchievementNotificationSystem = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [activeNotification, setActiveNotification] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const lastNotificationTimeRef = useRef(0);
  const unsubscribeRef = useRef(null);
  
  // Configure which notification types are enabled - not using state to avoid re-renders
  const enabledNotificationTypes = {
    [NOTIFICATION_TYPES.ACHIEVEMENT_UNLOCKED]: true,
    [NOTIFICATION_TYPES.RANK_UP]: true,
    [NOTIFICATION_TYPES.STREAK_MILESTONE]: true,
    [NOTIFICATION_TYPES.POINTS_EARNED]: false // Disable frequent point notifications
  };

  // Helper to format time elapsed
  const formatTimeElapsed = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const now = new Date();
      const eventTime = timestamp.toDate();
      const diffMs = now - eventTime;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch (error) {
      return '';
    }
  };

  // Handle dismissing the notification
  const dismissNotification = useCallback(() => {
    setShowNotification(false);
    
    // After animation completes, set the next notification
    setTimeout(() => {
      if (notifications.length > 0) {
        // Mark current as read
        if (activeNotification) {
          // Update in local state
          setNotifications(prev => 
            prev.map(n => n.id === activeNotification.id ? { ...n, read: true } : n)
          );
          
          // Also update in Firestore
          markNotificationAsRead(userId, activeNotification.id);
        }
        
        // Find next unread notification
        const nextUnread = notifications.find(n => !n.read && n.id !== activeNotification?.id);
        
        // Check if we should show the next notification (respecting cooldown)
        const now = Date.now();
        if (nextUnread && now - lastNotificationTimeRef.current > NOTIFICATION_COOLDOWN) {
          setActiveNotification(nextUnread);
          setShowNotification(true);
          lastNotificationTimeRef.current = now;
        } else {
          setActiveNotification(null);
        }
      } else {
        setActiveNotification(null);
      }
    }, 300); // Matches transition duration
  }, [activeNotification, notifications, userId]);

  // Auto-dismiss notifications after a delay
  useEffect(() => {
    if (showNotification && activeNotification) {
      const timer = setTimeout(() => {
        dismissNotification();
      }, 5000); // 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showNotification, activeNotification, dismissNotification]);

  // Subscribe to user events - with optimized setup to prevent infinite re-renders
  useEffect(() => {
    // Clean up previous listener if it exists
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    if (!userId) return;
    
    try {
      // Get recent events
      const eventsRef = collection(db, 'users', userId, 'events');
      const oneHourAgo = new Date();
      oneHourAgo.setHours(oneHourAgo.getHours() - 1);
      
      // Create a query that gets both documents where read is false or where read field doesn't exist
      const q = query(
        eventsRef,
        where('timestamp', '>=', Timestamp.fromDate(oneHourAgo)),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      
      // Set up the snapshot listener with explicit next/error handlers
      unsubscribeRef.current = onSnapshot(
        q, 
        (snapshot) => {
          const tempEvents = [];
          
          snapshot.forEach(doc => {
            try {
              const eventData = doc.data();
              
              // Skip if already marked as read
              if (eventData.read === true) {
                return;
              }
              
              // Filter notifications by type and thresholds
              if (Object.values(NOTIFICATION_TYPES).includes(eventData.type) && 
                  enabledNotificationTypes[eventData.type]) {
                
                // Apply point threshold for POINTS_EARNED events
                if (eventData.type === NOTIFICATION_TYPES.POINTS_EARNED && 
                    (eventData.data?.amount || 0) < MIN_POINTS_FOR_NOTIFICATION) {
                  return; // Skip small point notifications
                }
                
                tempEvents.push({
                  id: doc.id,
                  ...eventData,
                  read: false, // Explicitly set to false
                });
              }
            } catch (err) {
              // Skip this event if it's malformed
              console.log("Error processing event:", err);
            }
          });
          
          // Process notifications without creating dependency cycles
          setNotifications(prevNotifications => {
            // Merge with existing notifications, avoiding duplicates
            const existingIds = new Set(prevNotifications.map(n => n.id));
            const uniqueNewEvents = tempEvents.filter(event => !existingIds.has(event.id));
            
            // If we have new events and not currently showing a notification, show one
            if (uniqueNewEvents.length > 0 && !activeNotification) {
              const now = Date.now();
              
              // Only show if enough time has passed since the last notification
              if (now - lastNotificationTimeRef.current > NOTIFICATION_COOLDOWN) {
                setActiveNotification(uniqueNewEvents[0]);
                setShowNotification(true);
                lastNotificationTimeRef.current = now;
              }
            }
            
            return [...uniqueNewEvents, ...prevNotifications].slice(0, 10);
          });
        },
        (error) => {
          console.log("Notification listener error:", error);
        }
      );
      
      // Clean up on unmount
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } catch (error) {
      console.error("Error setting up notification listener:", error);
    }
  }, [userId, activeNotification]); // Added activeNotification to dependencies

  // Render the appropriate notification content based on type
  const renderNotificationContent = (notification) => {
    if (!notification) return null;
    
    switch (notification.type) {
      case NOTIFICATION_TYPES.ACHIEVEMENT_UNLOCKED:
        return (
          <>
            <div className="p-1.5 bg-emerald-500/20 rounded-full">
              <Award className="w-4 h-4 text-emerald-400" />
            </div>
            
            <div className="flex-1">
              <h4 className="text-emerald-300 text-sm font-medium">Achievement Unlocked!</h4>
              {notification.data?.achievements && notification.data.achievements.length > 0 && (
                <p className="text-white/70 text-xs">
                  {notification.data.achievements[0].name}
                </p>
              )}
              
              {notification.data?.bonusPoints > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-300 text-xs">+{notification.data.bonusPoints} pts</span>
                </div>
              )}
            </div>
          </>
        );
        
      case NOTIFICATION_TYPES.RANK_UP:
        return (
          <>
            <div className="p-1.5 bg-amber-500/20 rounded-full">
              <Trophy className="w-4 h-4 text-amber-400" />
            </div>
            
            <div className="flex-1">
              <h4 className="text-amber-300 text-sm font-medium">Rank Up!</h4>
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <span>{notification.data?.oldRank}</span>
                <ChevronRight className="w-3 h-3" />
                <span className="text-amber-300 font-medium">{notification.data?.newRank}</span>
              </div>
            </div>
          </>
        );
        
      case NOTIFICATION_TYPES.STREAK_MILESTONE:
        return (
          <>
            <div className="p-1.5 bg-blue-500/20 rounded-full">
              <Star className="w-4 h-4 text-blue-400" />
            </div>
            
            <div className="flex-1">
              <h4 className="text-blue-300 text-sm font-medium">Streak Milestone!</h4>
              <p className="text-white/70 text-xs">
                {notification.data?.streak} day streak achieved
              </p>
            </div>
          </>
        );
        
      case NOTIFICATION_TYPES.POINTS_EARNED:
        return (
          <>
            <div className="p-1.5 bg-green-500/20 rounded-full">
              <Star className="w-4 h-4 text-green-400" />
            </div>
            
            <div className="flex-1">
              <h4 className="text-green-300 text-sm font-medium">Points Earned</h4>
              <p className="text-white/70 text-xs">
                +{notification.data?.amount} points {notification.data?.reason !== 'multiple_actions' ? 
                  `from ${notification.data?.reason?.replace(/_/g, ' ')}` : ''}
              </p>
            </div>
          </>
        );
        
      default:
        return (
          <div className="text-white/70 text-xs">New notification received</div>
        );
    }
  };

  // Don't render anything if there are no notifications
  if (!activeNotification || notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-3 right-3 z-50 max-w-xs">
      {showNotification && (
        <div className="bg-slate-800 border border-emerald-500/30 rounded-lg shadow-lg p-3 transform transition-all duration-300 ease-in-out animate-slideUp">
          <div className="flex items-start gap-2">
            {renderNotificationContent(activeNotification)}
            
            <button
              onClick={dismissNotification}
              className="text-white/50 hover:text-white p-0.5"
              aria-label="Dismiss notification"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          
          {activeNotification.timestamp && (
            <div className="mt-1 text-right text-white/40 text-[10px]">
              {formatTimeElapsed(activeNotification.timestamp)}
            </div>
          )}
        </div>
      )}
      
      {/* Indicator showing there are more notifications */}
      {notifications.filter(n => !n.read).length > 1 && (
        <div className="mt-1 text-center">
          <span className="bg-emerald-500/20 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded-full inline-block">
            +{notifications.filter(n => !n.read).length - 1} more
          </span>
        </div>
      )}
    </div>
  );
};

export default AchievementNotificationSystem;